import React, { useContext, useState } from 'react';
import { Card } from '../../../../../components/Card/Card';
import { Icon } from '../../../../../components/Icon/Icon';
import { SortableList } from '../../../../../components/SortableList/SortableList';
import { t } from '../../../../../locales/translate';

import { useQueryHandler, useMutationHandler } from '../../../../../model/graphql';
import { graphql } from '../../../../../generated';
import { formatDateTime } from '../../../../../util/formatters';
import { Link } from 'react-router-dom';
import { Stack } from '../../../../../components/Stack/Stack';
import { orderPath } from '../../../../../routes';
import { Tag } from '../../../../../components/Tag/Tag';
import { useSmartConfirmation } from '../../../../../components/ConfirmationPrompt/ConfirmationPrompt';

import { CursorPagination, type RowsPerPage } from '../../../../../components/cursor_pagination/cursor_pagination';
import { NetworkStatus } from '@apollo/client';
import { ActivitiesContext } from '../../../../../components/Activities/Activities';

const RESALE_OFFERS = graphql(`
  query ResaleOffers($id: ID!, $first: Int, $cursor: String) {
    product(id: $id) {
      id
      resaleOffers(first: $first, after: $cursor) {
        nodes {
          id
          originalOrderReference
          originalOrderId
          createdAt
          soldItemValidationCode
          soldItemId
          status
          purchasedAt
          newOrderId
          public
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          endCursor
        }
      }
    }
  }
`);

const CANCEL_RESALE_OFFER = graphql(`
  mutation CancelResaleOffer($id: ID!) {
    cancelResaleOffer(input: { itemId: $id }) {
      item {
        id
      }
    }
  }
`);

interface Props {
  eventId: string;
}

const statusMap = {
  purchased: {
    label: t.model.event.resale.offer.purchased,
    bgColor: '#74DE98',
  },
  cart: { label: t.model.event.resale.offer.cart, bgColor: '#343434' },
  on_sale: { label: t.model.event.resale.offer.available, bgColor: '#FBCF5F' },
};

export function ResaleOverview({ eventId }: Props): JSX.Element {
  const { reloadActivities } = useContext(ActivitiesContext);
  const [rows, setRows] = useState<RowsPerPage>('10');
  const { data, refetch, networkStatus } = useQueryHandler(RESALE_OFFERS, {
    variables: {
      id: eventId,
      first: parseInt(rows),
      cursor: null,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const loading = networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables;

  const pageInfo = data?.product?.resaleOffers.pageInfo;

  const [cancelResaleOffer] = useMutationHandler(CANCEL_RESALE_OFFER);

  const confirmDelete = useSmartConfirmation();

  const resaleOffersItems = (data?.product?.resaleOffers?.nodes || []).map((offer) => {
    return {
      orderReference: offer?.originalOrderReference,
      onOfferSince: formatDateTime(offer?.createdAt),
      soldItemValidationCode: offer?.soldItemValidationCode,
      status: offer?.status,
      visibility: offer?.public ? t.model.event.resale.offer.publicOffer : t.model.event.resale.offer.privateLink,
      trash: null,
      originalOrderId: offer?.originalOrderId,
      newOrderId: offer?.newOrderId,
      purchasedAt: formatDateTime(offer?.purchasedAt),
      id: offer?.id,
      soldItemId: offer?.soldItemId,
    };
  });

  const showTag = (row: (typeof resaleOffersItems)[0]): boolean => {
    return row.status && statusMap[row.status];
  };

  return (
    <Card
      title={t.model.event.resale.offer.cardHeader}
      footer={
        <CursorPagination
          hasNextPage={pageInfo?.hasNextPage}
          hasPreviousPage={pageInfo?.hasPreviousPage}
          rowsPerPage={rows}
          onRowsPerPageChange={setRows}
          onFirstPageClick={() => refetch({ cursor: null })}
          onNextPageClick={() => {
            refetch({ cursor: pageInfo?.endCursor });
          }}
        />
      }
    >
      <SortableList
        loading={loading}
        items={resaleOffersItems}
        columns={[
          {
            label: t.model.event.resale.offer.orderReference,
            dataKey: 'orderReference',
            cellRenderer: (row) => (
              <Link style={{ textDecoration: 'underline' }} to={orderPath(row?.originalOrderId ?? '')}>
                {row.orderReference}
              </Link>
            ),
          },
          {
            label: t.model.event.resale.offer.onOfferSince,
            dataKey: 'onOfferSince',
          },
          {
            label: t.model.event.resale.offer.validationCode,
            dataKey: 'soldItemValidationCode',
          },
          {
            label: t.model.event.resale.offer.status,
            dataKey: 'status',
            cellRenderer: (row) => (
              <Stack line size="sm">
                {showTag(row) && (
                  <Tag
                    squared
                    text={statusMap[row.status ?? ''].label}
                    backgroundColor={statusMap[row.status ?? ''].bgColor}
                  />
                )}
                {row.newOrderId && row.purchasedAt && (
                  <Link style={{ textDecoration: 'underline' }} to={orderPath(row.newOrderId)}>
                    {row.purchasedAt}
                  </Link>
                )}
              </Stack>
            ),
          },
          {
            label: t.model.event.resale.offer.visibility,
            dataKey: 'visibility',
          },
          {
            label: '',
            dataKey: 'trash',
            cellRenderer: (row) =>
              row.status === 'on_sale' ? (
                <Icon
                  onClick={() => {
                    confirmDelete({
                      title: t.general.confirmDelete(t.model.event.resale.offer.resaleOffer),
                      action: async () => {
                        await cancelResaleOffer({
                          variables: {
                            id: row.soldItemId || '',
                          },
                        });
                        refetch();
                        reloadActivities();
                      },
                    });
                  }}
                  name="trash"
                  color="#909090"
                />
              ) : null,
          },
        ]}
      />
    </Card>
  );
}
